/**
 * m3 echo Frontend - Mandanten-Tabelle
 *
 * Tabellenkomponente zur Anzeige und Verwaltung von Mandanten.
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { TenantWithStats } from '../../services/admin';
import { formatMoney } from '../../utils/currency';
import QuotaBar from '../quota/QuotaBar';

interface TenantTableProps {
  tenants: TenantWithStats[];
  onEdit: (tenant: TenantWithStats) => void;
  onDelete: (tenant: TenantWithStats) => void;
  onView?: (tenant: TenantWithStats) => void;
  onToggleStatus?: (tenant: TenantWithStats) => void;
}

export const TenantTable: React.FC<TenantTableProps> = ({
  tenants,
  onEdit,
  onDelete,
  onView,
  onToggleStatus,
}) => {
  if (tenants.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          Keine Mandanten gefunden
        </Typography>
      </Paper>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell><strong>Name</strong></TableCell>
            <TableCell><strong>Anzeigename</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell align="right"><strong>Benutzer</strong></TableCell>
            <TableCell align="right"><strong>Projekte</strong></TableCell>
            <TableCell align="center">
              <strong>Quota</strong>
              {/*
                The key to the three figures per row, stated once here instead
                of on every row. Same order as the bar draws its segments.
              */}
              <Typography
                variant="caption"
                component="div"
                sx={{ color: 'text.secondary', fontWeight: 400, lineHeight: 1.3 }}
              >
                gebucht / verbraucht / gesamt
              </Typography>
            </TableCell>
            <TableCell align="right"><strong>Gesamtkosten</strong></TableCell>
            <TableCell align="center"><strong>Aktionen</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tenants.map((tenant) => (
            <TableRow key={tenant.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {tenant.name}
                </Typography>
              </TableCell>
              <TableCell>{tenant.display_name}</TableCell>
              <TableCell>
                {tenant.is_system ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Aktiv"
                    color="success"
                    size="small"
                  />
                ) : (
                  <Tooltip title={tenant.is_active ? 'Klicken zum Deaktivieren' : 'Klicken zum Aktivieren'}>
                    <Chip
                      icon={tenant.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                      label={tenant.is_active ? 'Aktiv' : 'Inaktiv'}
                      color={tenant.is_active ? 'success' : 'default'}
                      size="small"
                      onClick={() => onToggleStatus?.(tenant)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">{tenant.user_count || 0}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">{tenant.project_count || 0}</Typography>
              </TableCell>
              <TableCell align="center">
                <Tooltip
                  title={`Diesen Monat ${tenant.monthly_prompts_reserved + tenant.monthly_prompts_consumed} von ${tenant.monthly_prompt_quota} Prompts belegt (${tenant.monthly_prompts_reserved} gebucht, ${tenant.monthly_prompts_consumed} verbraucht). Reset am 1. des Folgemonats in Mandanten-Zeitzone.`}
                  arrow
                >
                  <Box>
                    <QuotaBar
                      compact
                      limit={tenant.monthly_prompt_quota}
                      label={`${tenant.display_name} Kontingent`}
                      segments={[
                        { key: 'reserved', value: tenant.monthly_prompts_reserved, label: 'gebucht', variant: 'reserved' },
                        { key: 'consumed', value: tenant.monthly_prompts_consumed, label: 'verbraucht', variant: 'consumed' },
                      ]}
                    />
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2">
                  {formatMoney(tenant.total_cost, tenant.currency)}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                  {onView && (
                    <Tooltip title="Details Anzeigen">
                      <IconButton
                        size="small"
                        onClick={() => onView(tenant)}
                        color="info"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Mandant Bearbeiten">
                    <IconButton
                      size="small"
                      onClick={() => onEdit(tenant)}
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Mandant Löschen">
                    <IconButton
                      size="small"
                      onClick={() => onDelete(tenant)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TenantTable;
